import { Injectable, inject } from '@angular/core';
import { HttpContext, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@env';
import {
  AcceptanceHistoryParams,
  AcceptanceWithdrawal,
  ConsentContentUrl,
  ConsentDocumentDetails,
  ConsentDocumentSummary,
  ConsentEventHistoryPage,
  ConsentSet,
  ConsentSetOverride,
  ConsentSetOverrideSummary,
  ConsentSetSummary,
  RecordWithdrawalParams,
} from '@shared/models';
import { BackendService } from '../backend/backend.service';
import { IS_EXTENDED_ERRORS } from '../../interceptors/error.interceptor';

const path = (...segments: string[]): string =>
  [environment.consentAdminEndpoint, ...segments.map((segment) => encodeURIComponent(segment))].join('/');

const historyParams = (params: AcceptanceHistoryParams): HttpParams =>
  Object.entries(params).reduce(
    (httpParams, [name, value]) =>
      value === undefined || value === null || value === '' ? httpParams : httpParams.set(name, String(value)),
    new HttpParams(),
  );

@Injectable({
  providedIn: 'root',
})
export class ConsentAdminService {
  private backendService = inject(BackendService);

  getDocuments(): Observable<ConsentDocumentSummary[]> {
    return this.backendService.get<ConsentDocumentSummary[]>(path('documents'));
  }

  getDocument(documentId: string): Observable<ConsentDocumentDetails> {
    return this.backendService.get<ConsentDocumentDetails>(path('documents', documentId)).pipe(
      map((document) => ({
        ...document,
        versions: [...(document.versions ?? [])].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')),
      })),
    );
  }

  getVersionContentUrl(documentId: string, version: string, language?: string): Observable<ConsentContentUrl> {
    const params = language ? new HttpParams().set('language', language) : undefined;
    return this.backendService.get<ConsentContentUrl>(path('documents', documentId, 'versions', version, 'content-url'), { params });
  }

  getConsentSets(): Observable<ConsentSetSummary[]> {
    return this.backendService.get<ConsentSetSummary[]>(path('consent-sets'));
  }

  getConsentSet(consentSetId: string): Observable<ConsentSet> {
    return this.backendService.get<ConsentSet>(path('consent-sets', consentSetId));
  }

  getOverrides(): Observable<ConsentSetOverrideSummary[]> {
    return this.backendService.get<ConsentSetOverrideSummary[]>(path('consent-set-overrides'));
  }

  getOverride(overrideId: string): Observable<ConsentSetOverride> {
    return this.backendService.get<ConsentSetOverride>(path('consent-set-overrides', overrideId));
  }

  recordWithdrawal({
    acceptanceId,
    businessAccountId,
    receivedAt,
    withdrawalChannel,
  }: RecordWithdrawalParams): Observable<AcceptanceWithdrawal> {
    const params = new HttpParams().set('businessAccountId', businessAccountId);
    return this.backendService.post<AcceptanceWithdrawal>(
      path('acceptances', acceptanceId, 'withdrawals'),
      { receivedAt, withdrawalChannel },
      { params, context: new HttpContext().set(IS_EXTENDED_ERRORS, true) },
    );
  }

  getWithdrawal(acceptanceId: string, businessAccountId: string): Observable<AcceptanceWithdrawal> {
    const params = new HttpParams().set('businessAccountId', businessAccountId);
    return this.backendService.get<AcceptanceWithdrawal>(path('acceptances', acceptanceId, 'withdrawals'), { params });
  }

  getAcceptanceHistory(params: AcceptanceHistoryParams): Observable<ConsentEventHistoryPage> {
    return this.backendService.get<ConsentEventHistoryPage>(path('consent-events'), { params: historyParams(params) });
  }

  // Sent as a filter, not a path segment, so a workflow id containing a slash still works.
  getAcceptancesByWorkflow(workflowId: string, params: AcceptanceHistoryParams): Observable<ConsentEventHistoryPage> {
    return this.getAcceptanceHistory({ ...params, workflowId });
  }
}
